'use client'

import type { CSSProperties, ReactNode } from 'react'
import Link from 'next/link'
import { ArrowLeft, Globe, LockKeyhole } from 'lucide-react'
import { LanguageToggle } from '@/components/LanguageToggle'
import { PublicThemeSelector } from '@/components/PublicThemeSelector'
import '@/app/public.css'

type AuthLayoutProps = {
  children: ReactNode
  title?: ReactNode
  subtitle?: ReactNode
  footer?: ReactNode
  backHref?: string
  backLabel?: ReactNode
  topActionHref?: string
  topActionLabel?: ReactNode
  brandHref?: string
  brandLabel?: ReactNode
  backgroundStyle?: CSSProperties
}

export function AuthLayout({
  children,
  title,
  subtitle,
  footer,
  backHref,
  backLabel,
  topActionHref,
  topActionLabel,
  brandHref = '/',
  brandLabel = 'BPAL',
  backgroundStyle,
}: AuthLayoutProps) {
  return (
    <div className="relative min-h-screen overflow-hidden px-4 py-3 sm:px-6" style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}>
      {backgroundStyle ? (
        <div aria-hidden className="pointer-events-none absolute inset-0" style={backgroundStyle} />
      ) : null}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-[-8rem] top-[-7rem] h-[18rem] w-[18rem] rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(46, 232, 154, 0.08), transparent 68%)' }} />
        <div className="absolute right-[-12rem] top-[14%] h-[24rem] w-[24rem] rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(46, 232, 154, 0.07), transparent 70%)' }} />
      </div>

      <div className="relative z-10 mx-auto flex min-h-[calc(100vh-1.5rem)] max-w-[1120px] flex-col">
        <header className="flex items-center justify-between gap-4 py-2">
          <Link href={brandHref} className="flex items-center gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl text-white shadow-[0_18px_50px_-26px_rgba(46,232,154,0.35)]" style={{ background: 'var(--accent)' }}>
              <LockKeyhole className="h-4 w-4" />
            </span>
            <span className="font-display text-[1.65rem] font-semibold tracking-tight" style={{ color: 'var(--accent)' }}>
              {brandLabel}
            </span>
          </Link>

          <div className="flex items-center gap-2 sm:gap-2.5">
            <div className="hidden items-center gap-1.5 rounded-2xl px-2.5 py-1.5 text-sm shadow-sm backdrop-blur md:flex" style={{ border: '1px solid var(--rule-bright)', backgroundColor: 'rgba(255, 255, 255, 0.1)', color: 'var(--text-muted)' }}>
              <Globe className="h-4 w-4" style={{ color: 'var(--accent)' }} />
              <LanguageToggle className="w-fit" />
            </div>
            <PublicThemeSelector />
            {topActionHref && topActionLabel ? (
              <Link
                href={topActionHref}
                className="inline-flex items-center justify-center rounded-2xl px-3 py-1.5 text-sm font-semibold transition hover:opacity-80"
                style={{ border: `1px solid var(--accent)`, color: 'var(--accent)', backgroundColor: 'var(--accent-dim)' }}
              >
                {topActionLabel}
              </Link>
            ) : null}
          </div>
        </header>

        <main className="flex flex-1 items-center justify-center py-3 sm:py-5">
          <div className="w-full max-w-[440px]">
            <div className="relative overflow-hidden rounded-[24px] px-3 pb-4 pt-3 shadow-[0_26px_80px_-54px_rgba(15,23,42,0.16)] backdrop-blur sm:px-4" style={{ border: `1px solid var(--rule-bright)`, backgroundColor: 'var(--bg-card)' }}>
              <div className="absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent, var(--accent), transparent)` }} />

              <div className="mb-2 flex items-center justify-between gap-2">
                {backHref ? (
                  <Link
                    href={backHref}
                    className="inline-flex items-center gap-1 rounded-full px-2 py-1.5 text-xs font-medium transition hover:opacity-80"
                    style={{ border: `1px solid var(--rule-bright)`, backgroundColor: 'var(--bg-card)', color: 'var(--text-muted)' }}
                  >
                    <ArrowLeft className="h-3 w-3" />
                    <span>{backLabel}</span>
                  </Link>
                ) : (
                  <div />
                )}
                <LanguageToggle className="w-fit md:hidden" />
              </div>

              {(title || subtitle) && (
                <div className="mx-auto mb-2 flex w-full max-w-[360px] flex-col items-center text-center">
                  <div className="mb-2 flex h-16 w-16 items-center justify-center rounded-[1.2rem] shadow-[inset_0_2px_16px_rgba(255,255,255,0.35)]" style={{ background: `radial-gradient(circle at 30% 30%, var(--accent), var(--accent-dark))`, boxShadow: '0 24px 60px -30px rgba(46, 232, 154, 0.3)' }}>
                    <div className="flex h-12 w-12 items-center justify-center rounded-[0.9rem] border border-white/35 bg-white/10 backdrop-blur">
                      <LockKeyhole className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  {title ? (
                    <h1 className="font-display text-[1.75rem] font-semibold leading-[0.98] tracking-tight sm:text-[2.1rem]" style={{ color: 'var(--text)' }}>
                      {title}
                    </h1>
                  ) : null}
                  {subtitle ? (
                    <p className="mt-1.5 max-w-[28ch] text-xs leading-5" style={{ color: 'var(--text-muted)' }}>
                      {subtitle}
                    </p>
                  ) : null}
                </div>
              )}

              <div className="mx-auto w-full max-w-[360px] space-y-3">{children}</div>

              {footer ? (
                <div className="mx-auto mt-4 w-full max-w-[360px] pt-3 text-center" style={{ borderTop: `1px solid var(--rule-bright)` }}>
                  {footer}
                </div>
              ) : null}
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}
